import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, Modal } from 'react-bootstrap';
import { CastType, IQuery, IWhere, IQuerySelectData } from '../solution/Interfaces';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateFull } from '../solution/utils/AppUtil';
import { Check, FileText, ScanLine, X, Printer, Pencil, Save, ChevronUp, ChevronDown, RotateCcw, CheckCircle } from 'lucide-react';
import ShowErrorAlert from '../ShowErrorAlert';


interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;
}

interface Props {
    store: IStore;
    onBack: () => void;
}

interface IIntransitIBT {
     id: string;
     created: string;
     created_by: string;
     updated: string | null;
     doc_num: string,
     category: string,
     internal: boolean;
     from_whs: string;
     notes: string | null;
     status: string;
     to_whs: string;
     nedap_id: string | null;
     completed_at?: string;
     extended_message?: string | null;
     function_call_id?: string;
     handled_status: string | null;
}

interface IIntransitIBTLine {
    id: string;
    created: string;
    updated: string | null;
    line_num: number;
    item_name: string;
    item_code: string;
    item_barcode: string;
    doc_num: string;
    qty_on_hand: number | null;
    qty_received: number | null;
    qty_scanned_out: number | null;
    qty_scanned_in: number | null;
    qty_sent: number | null;
}

interface ISelectedScannedItem {
  id: string;
  barcode: string;
  item_code: string;
  item_group: string;
  name: string;
  product_code: string;
}


export const IntransitIBT: React.FC<Props> = ({ store, onBack }) => {

  const { fetchData, responseErrorMessage, showErrorAlert, responseStatus, userDetail }  = useAuth();
  const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
  const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
  const fetchIntrasitIBTActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-ACTIONS/run`; 
  const fetchIntransitIBTLineActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/INTERBRANCH-TRANSFER-LINE-ACTIONS/run`; 


  const [intransitIBTState, setIntransitIBTState] = useState<IIntransitIBT[]>([]);
  const [intransitIBTFailedTransactionState, setIntransitIBTFailedTransactionState] = useState<IIntransitIBT[]>([]);
  const [intransitIBTLineState, setIntransitIBTLineState] = useState<IIntransitIBTLine[]>([]);
  const [selectedIntransitIBT, setSelectedIntransitIBT] = useState<IIntransitIBT | null>(null);
  const [selectedIntransitIBTFailedTransaction, setSelectedIntransitIBTFailedTransaction] = useState<IIntransitIBT | null>(null);
  const [selectedDocNum, setSelectedDocNum] = useState<string>("");
   
  const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
  const [submitRefreshLineData, setSubmitRefreshLineData] = useState<boolean>(false);
  const [submitRefreshFailedTransactionData, setSubmitRefreshFailedTransactionData] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'scan' | 'view' | 'default'>('default'); 
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFailed, setLoadingFailed] = useState<boolean>(false);

  const [showUpdateStatusNormalIBTModal, setShowUpdateStatusNormalIBTModal] = useState<boolean>(false);

  //refs 
  const fileRef = useRef<HTMLInputElement | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);

  const [selectedScannedItem, setSelectedScannedItem] = useState<ISelectedScannedItem | null>(null);  
  const [showCustomQuantitySection, setShowCustomQuantitySection] = useState<boolean>(false);
  const [customQuantity, setCustomQuantity] = useState<string>('1');

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editedQty, setEditedQty] = useState<Record<string, number>>({}); 
  const [inputDraft, setInputDraft] = useState<Record<string, string>>({});

  const [nedapFilter, setNedapFilter] = useState<'ALL' | 'NEDAP' | 'NON_NEDAP'>('ALL');
  const [expanded, setExpanded] = useState(false);

  const [showFailedIntransitIBTRerunModal, setShowFailedIntransitIBTRerunModal] = useState<boolean>(false);
  const [showFailedIntransitIBTHandledModal, setShowFailedIntransitIBTHandledModal] = useState<boolean>(false);

    // -> failed transactions
    const defaultQueryStateFailedTransactions: IQuerySelectData = {
        appCode: "ares",
        selectCode: "sa-ares-interbranch-transfer",  
        where: `to_whs = :to_whs AND internal = :internal AND status IN (:status) AND handled_status IS NOT DISTINCT FROM :handled_status`,  
        params: {'to_whs': `${store?.code}`, 'internal': false, 'status': ["SENT", "CLOSED", "RECEIVED"],  'handled_status': "REQUIRED" }
    }; 
    const [querySelectStateFailed, setQuerySelectStateFailed] = useState<IQuerySelectData>(defaultQueryStateFailedTransactions);

    // -> successful transactions
    const defaultQueryStateSuccessfulTransactions: IQuerySelectData = {
        appCode: "ares",
        selectCode: "sa-ares-interbranch-transfer",  
        where: `to_whs = :to_whs AND internal = :internal AND status IN (:status)`,  
        params: {'to_whs': `${store?.code}`, 'internal': false, 'status': ["SENT", "CLOSED", "RECEIVED"]}
    }; 
    const [querySelectStateSuccessful, setQuerySelectStateSuccessful] = useState<IQuerySelectData>(defaultQueryStateSuccessfulTransactions);
    

  //Query Line State
  const [queryLineState, setQueryLineState] = useState<IQuery>({
      tableName: "sa_ares_interbranch_transfer_line",
      select: [],
      groupBy: [],
      join: [{
        relationship: 'item',
        joinType: "LEFT"
      }],
      orderBy: [ 
          {
              fullColumnName: "created",
              direction: "ASC",
              cast: CastType.TIMESTAMP
          }
      ],
      where:[],
      mainTableOnly: false,
      includeExtraColumns: true,
      includeControlColumns: true,
      modifiedAfter: null,
      touchedBefore: null,
      isDirty: null,
    //   pageNum: 0,
    //   pageSize: 100,
      frontEndOnly: null
  });
  

  const defaultQueryState: IQuerySelectData = {
          appCode: "ares",
          selectCode: "scan-item",  
          params: {}
      };   
        
  const [querySelectState, setQuerySelectState] = useState<IQuerySelectData>(defaultQueryState);

   useEffect(() => {
        setLoading(true); 
        fetchData(queryUrlSelectNoCount, {method:"POST", body: JSON.stringify(querySelectStateSuccessful)}, processData, cleanUpLoader);
   }, [submitRefreshData]);

    useEffect(() => {
        if(expanded){
          setLoadingFailed(true);
          fetchData(queryUrlSelectNoCount, {method:"POST", body: JSON.stringify(querySelectStateFailed)}, processDataFailed, cleanUpLoader);
        }
        
    }, [expanded, submitRefreshFailedTransactionData]);

    useEffect(() => {
            if (viewMode === "scan" && scanRef.current) {
                scanRef.current.focus();
            }
    }, [viewMode]);
    
    useEffect(() => {
        if(selectedIntransitIBT){
            const queryLine = {...queryLineState}
            queryLine.where = [
                    {
                    fullColumnName: "doc_num",
                    value: selectedIntransitIBT?.doc_num, 
                    operator: "EQ",
                    cast: CastType.STRING
                }       
            ]
            setLoading(true); 
            fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryLine)}, processLineData, cleanUpLoader);
        }
    }, [selectedIntransitIBT, submitRefreshLineData]);


  const processData = (result: any) => {

      const intransitIBT: IIntransitIBT[] = [];

      for(const key in result?.data){
          intransitIBT.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
      }

      // Sort by created date descending
      intransitIBT.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      setIntransitIBTState(intransitIBT);
  }

    const processDataFailed = (result: any) => {

      const intransitIBTFailedTransactions: IIntransitIBT[] = [];

      for(const key in result?.data){
          intransitIBTFailedTransactions.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
      }

      // Sort by created date descending
      intransitIBTFailedTransactions.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      setIntransitIBTFailedTransactionState(intransitIBTFailedTransactions);
    }


   const processLineData = (result: any) => {

        const normalIBTLines: IIntransitIBTLine[] = [];

        for(const key in result?.data){

            const entry = result.data[key];
            const control = entry.control || {};
            const data = entry.data || {};

            normalIBTLines.push({
                id: control.id,
                created: control.created,
                updated: control.updated ?? null,
                line_num: data.line_num,
                item_name: data["item.name"],
                item_code: data.item_code,
                item_barcode: data["item.barcode"], 
                doc_num: data.doc_num,
                qty_on_hand: data.qty_on_hand,
                qty_received: data.qty_received,
                qty_scanned_out: data.qty_scanned_out,
                qty_scanned_in: data.qty_scanned_in,
                qty_sent: data.qty_sent,
                });
        }
        
        setIntransitIBTLineState(normalIBTLines);
  };

  //default clean up loader
  const cleanUpLoader = () => {
      setLoading(false);
      setLoadingFailed(false)
  }

  //handle view button when clicked
  const handleViewClick = (intransitIBT : IIntransitIBT) => {
      setViewMode('view'); // Switch to view mode
      setSelectedIntransitIBT(intransitIBT);
  }

  //handle scan button when clicked
  const handleScanClick = (intransitIBT : IIntransitIBT) => {
      setViewMode('scan'); // Switch to Scan mode
      setSelectedIntransitIBT(intransitIBT);
  }

  // Add the event handler for "Enter" key press
  const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, docNum: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();  // Prevent default behavior (if needed)
            handleScanSubmit(docNum); 
        }
  };


  const handleScanSubmit = (docNum: string) => {

        setLoading(true);
        const scanValueItemCode = scanRef.current?.value.trim();
        if (!docNum || !scanValueItemCode) return;

        const queryPayload = {
          ...querySelectState,
          params: { ...querySelectState.params, scan_code: scanValueItemCode },
        };

        let shouldRunLineFetch = true;

        fetchData( queryUrlSelectNoCount,
          { method: "POST", body: JSON.stringify(queryPayload)},
          (result: any, status: string) => {
            if (status === "200") {
              const item = result?.data?.[0]?.data;
              const id = result?.data?.[0]?.id;

              if (item && id) {

                const itemGroup = item.item_group;

                const scannedItem: ISelectedScannedItem = {
                  id,
                  barcode: item.barcode,
                  item_code: item.item_code,
                  item_group: itemGroup,
                  name: item.name,
                  product_code: item.product_code,
                };

                setSelectedScannedItem(scannedItem);

                if (itemGroup === "Jibbitz") {
                  setShowCustomQuantitySection(true);
                  shouldRunLineFetch = false; // Don't run line fetch for Jibbitz
                }
              }
            }

            // Only run line fetch if allowed
            if (shouldRunLineFetch) {
             fetchData(fetchIntransitIBTLineActionsQueryUrl,
              { method:"POST", body: JSON.stringify({ doc_num: docNum, item_code: scanValueItemCode, qty_scanned_in: 1 })}, 
              (result: any, status: string) =>  { 
                if(status === "200"){
                   setSubmitRefreshLineData(prev => !prev); 
                }
              }, cleanUpLoader);
            } 
            else {
              cleanUpLoader(); // still clean up if skipping line fetch
            }
          }
        );

        if (scanRef.current) {
          scanRef.current.value = "";
        }
    };


    const handleQuantitySubmit = () => {

      if (!selectedScannedItem || !selectedIntransitIBT?.doc_num) return;

      const { item_code, barcode } = selectedScannedItem;

      const parsedQty = Math.max(1, parseInt(customQuantity) || 1);

      const payload = {
        doc_num: selectedIntransitIBT.doc_num,
        qty_scanned_in: parsedQty,
        item_code: item_code || barcode,
      };

      setLoading(true);

      fetchData(fetchIntransitIBTLineActionsQueryUrl,
          { method:"POST", body: JSON.stringify(payload)}, 
          (result: any, status: string) =>  { 
            if(status === "200"){
                 setShowCustomQuantitySection(false);
                 setSelectedScannedItem(null);
                 setCustomQuantity('1');
                 setSubmitRefreshLineData(prev => !prev); 
            }
          }, cleanUpLoader);
    };

   //handle to receieved ibt button when clicked
    const handleReceivedStatusIntransitIBTClick = (intransitIBT : IIntransitIBT) => {
        setViewMode('default'); // Switch to default mode
        setShowUpdateStatusNormalIBTModal(true);
        setSelectedDocNum(intransitIBT.doc_num);
    }

    //handle the submit for setting re normal ibt
    const handleRecievedIntransitIBTSubmit = () => {
        if(selectedDocNum !== ""){
            setLoading(true);
            fetchData(fetchIntrasitIBTActionsQueryUrl, 
                {method:"POST", body: JSON.stringify({ doc_num: selectedDocNum,  status: "RECEIVED" })}, 
                (result: any, status: string) =>  {
                    if(status === "200"){
                       // Causes refresh on data  
                       setSubmitRefreshData(prev => !prev);
                    }
                }, cleanUpLoader);
            setSelectedDocNum("");
        }else{
            return; 
        }
    }

    const handleEditClick = (id: string, initialQty: number) => {
        setEditingRowId(id);
        setEditedQty(prev => ({ ...prev, [id]: initialQty }));
        setInputDraft(prev => ({ ...prev, [id]: initialQty.toString() }));
    };

    const handleQtyDraftChange = (id: string, value: string) => {
        setInputDraft(prev => ({ ...prev, [id]: value }));
    };

    const handleBlur = (id: string) => {
        const draftValue = inputDraft[id];
        const parsed = parseInt(draftValue, 10);
        setEditedQty(prev => ({ ...prev, [id]: isNaN(parsed) ? 0 : parsed }));
    };


    const handleSaveClick = (id: string) => {
        const qty = editedQty[id];
        const item_code = intransitIBTLineState.find(line => line.id === id)?.item_code;
        const doc_num = selectedIntransitIBT?.doc_num;

        if (!item_code || qty === undefined || doc_num === undefined) return;

        const payload = {
        doc_num,
        item_code,
        qty_scanned_in: qty,
        mode: 'exact'
        };


        // Perform your save operation here (e.g., API call)
        fetchData(fetchIntransitIBTLineActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
                setSubmitRefreshLineData(prev => !prev); 
                setEditingRowId(null); // exit edit mode
                setEditedQty({});
                setInputDraft({});
            }
        }, cleanUpLoader);
    };


   //handle the failed transaction click rerun
    const handleFailedIntransitIBTRerunClick = (intransitIBT : IIntransitIBT) => {
        setViewMode('default'); // Switch to default mode
        setSelectedIntransitIBTFailedTransaction(intransitIBT);
        setShowFailedIntransitIBTRerunModal(true);
    }

    const handleFailedIntransitIBTRerunResetClick = () => {
       setSelectedIntransitIBTFailedTransaction(null);
       setShowFailedIntransitIBTRerunModal(false);
    }

    const handleFailedIntransitIBTRerunSubmit = () => {
        if(selectedIntransitIBTFailedTransaction?.doc_num !== "" && selectedIntransitIBTFailedTransaction?.function_call_id != null){
            setLoading(true);
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedIntransitIBTFailedTransaction?.function_call_id}/rerun`, 
            {method:"POST", body: JSON.stringify({  })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedIntransitIBTFailedTransaction(null);
                    setSubmitRefreshFailedTransactionData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedIntransitIBTFailedTransaction(null);
                }
                
            }, cleanUpLoader);
                
        } else {
            return; 
        }
    }

    // //handle the failed transaction click handled
    const handleFailedIntransitIBTHandledClick = (intransitIBT : IIntransitIBT) => {
        setViewMode('default'); // Switch to default mode
        setSelectedIntransitIBTFailedTransaction(intransitIBT);
        setShowFailedIntransitIBTHandledModal(true);
    }

    const handleFailedIntransitIBTHandledResetClick = () => {
        setSelectedIntransitIBTFailedTransaction(null);
        setShowFailedIntransitIBTHandledModal(false);
    }

    const handleFailedIntransitIBTHandledSubmit = () => {
          if(selectedIntransitIBTFailedTransaction?.doc_num !== "" && selectedIntransitIBTFailedTransaction?.function_call_id != null){
            setLoading(true);
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedIntransitIBTFailedTransaction?.function_call_id}/handle`, 
            {method:"POST", body: JSON.stringify({  })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedIntransitIBTFailedTransaction(null);
                    setSubmitRefreshFailedTransactionData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedIntransitIBTFailedTransaction(null);
                }
                
            }, cleanUpLoader);
                
        } else {
            return; 
        }
    }


  const DisabledScanBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Scan"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
              >
              <ScanLine style={{ width: "17px", height: "17px", color: "#6c757d" }} />
            </span>
        ) 
    } 

    const DisabledCheckBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Submit"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Check style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledEditQtyBtn = () => {
            return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Edit Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Pencil style={{ width: "17px", height: "17px", color: "#6c757d" }} />
            </span>
        )
    } 

    const DisabledSaveQtyBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Save Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Save style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        )
    } 



      //handles the back to intransit ibt.
    const handleBackToIntransitIBTS = () => {
        setViewMode('default');
        setSelectedIntransitIBT(null);
        setShowCustomQuantitySection(false);
        setSelectedScannedItem(null);
        setCustomQuantity('1');
        setEditingRowId(null); // exit edit mode
        setEditedQty({});
        setInputDraft({});
 
        fetchData(queryUrlSelectNoCount, {method:"POST", body: JSON.stringify(querySelectStateSuccessful)}, processData, cleanUpLoader);
    }
   
     const handleCloseForm = () => {
      setSelectedScannedItem(null);
      setCustomQuantity('1');
      setShowCustomQuantitySection(false); 
    };


     const thStyle = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const thNewStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        borderTop: "1px solid #dee6ed",
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const tdStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };

    const toggleExpanded = () => setExpanded((prev) => !prev);

    const NedapIDCell = ({ nedap_id }: { nedap_id: string | null }) => {
        const [open, setOpen] = useState(false);

        if (!nedap_id) return <td>No</td>;

        return (
            <td style={{...tdStyle, position: "relative"}}>
            <span onClick={() => setOpen(!open)} style={{ cursor: "pointer", textDecoration: "underline" }}>
                Yes
            </span>
            {open && (
                <div style={{ position: "absolute", top: "1px", left: "40px", backgroundColor: "#f8f9fa", border: "1px solid #ccc", padding: "4px 8px", fontSize: "12px", whiteSpace: "nowrap", userSelect: "text", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", zIndex: 10 }}>
                {nedap_id}
                </div>
            )}
            </td>
        );
    };

  return (
    <>
    { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
    <Card>

      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">{`Receive Stock (IBT) [${store.name}]`}</Card.Title>
        { viewMode === "default" && (
            <button
            className="btn p-1 d-flex align-items-center justify-content-center"
            onClick={toggleExpanded}
            title={expanded ? "Hide failed transactions" : "Show failed transactions"}
            style={{ border: "1px solid #c10000", background: "transparent", borderRadius: "50%", color: "#c10000", width: "22px", height: "22px"}}
            >
            {expanded ? (
                <ChevronUp size={20} strokeWidth={2.2} />
            ) : (
                <ChevronDown size={20} strokeWidth={2.2} />
            )}
            </button>
        )}
    </Card.Header>
     <CardBody>
        <div className="d-flex gap-1">
              
        </div>

         <div className="d-flex justify-content-between align-items-center mb-2">
            {/* Left side: Buttons */}
            <div className="d-flex gap-1">
               <button
                    className="btn btn-secondary h-100"
                    onClick={() => {
                        if (viewMode === 'default') {
                            onBack();
                        } else {
                            handleBackToIntransitIBTS();
                        }
                        }}
                    >
                    { viewMode === 'default' ? "Back To Stock Transfers (IBT)" : "Back To Receive Stock IBT" }
              </button>
            </div>

            {/* Right side: Dropdown filter */}
            {viewMode === 'default' && (
                <div>
                    <select
                        className="form-select form-select-md"
                        style={{ maxWidth: '200px' }}
                        value={nedapFilter}
                        onChange={(e) => setNedapFilter(e.target.value as any)}
                    >
                        <option value="ALL">All</option>
                        <option value="NEDAP">RFID</option>
                        <option value="NON_NEDAP">Non-RFID</option>
                    </select>
                </div>
            )}
          </div>


            {expanded && intransitIBTFailedTransactionState.length > 0 && viewMode === "default" && (
            <div style={{border: "1px solid #c10000" , padding: '0px 4px', marginBottom: '14px', marginTop: '14px', borderRadius: '5px'}}>
                <h5 className="mt-2 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                    Failed Transactions: 
                </h5>
            <div
            style={{
                maxHeight: intransitIBTFailedTransactionState.length > 10 ? "450px" : "unset", // change based on count
                overflowY: intransitIBTFailedTransactionState.length > 10 ? "auto" : "visible",
                marginBottom: "4px",
                position: "relative",
                backgroundColor: "#fff", // helps hide any pixel bleed
                borderBottom: "1px solid #dee6ed",
            }}
            >
            <table className="table table-bordered  mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa", boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.05)" }}>
                <tr>
                <th style={{ ...thNewStyle, width: "160px"}}>Doc Num</th>
                <th style={thNewStyle}>Message</th>
                <th style={thNewStyle}>Last Run</th>
                <th style={{ ...thNewStyle, width: "100px"}}>
                    Actions
                </th>
                </tr>
            </thead>
            <tbody>
                { loadingFailed && viewMode === "default" ? (
                <>
                    <tr>
                        <td colSpan={3} className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        </td>
                    </tr>
                </>
                ) : (
                    <>
                    {!selectedIntransitIBT && viewMode === "default" && intransitIBTFailedTransactionState.map((intransitIBT: IIntransitIBT, index) => (
                    <tr key={index}>
                        <td style={tdStyle}>{intransitIBT.doc_num}</td>
                        <td style={{...tdStyle,
                                maxWidth: "300px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}>
                            {intransitIBT.extended_message}
                        </td>
                        <td style={tdStyle}>{formatDateFull(intransitIBT.completed_at ?? "")}</td>
                        <td style={tdStyle} className="d-flex gap-1">
                        {/* Rerun */}
                        <button className="btn btn-secondary d-flex align-items-center gap-1" title="Rerun"
                            onClick={() => handleFailedIntransitIBTRerunClick(intransitIBT)}
                        >
                            <RotateCcw  style={{ width: "17px", height: "17px" }}/>
                        </button>

                        {/* Handled */}
                        <button className="btn btn-secondary d-flex align-items-center gap-1" title="Handled"
                            onClick={() => handleFailedIntransitIBTHandledClick(intransitIBT)}
                        >
                            <CheckCircle style={{ width: "17px", height: "17px" }} />
                        </button>
                        </td>
                    </tr>
                    
                    ))}
                    </>
                    )}
                </tbody>
                </table>
            </div>
            <div style={{marginBottom: "3px", marginLeft: "10px", color: "#c10000" }}>
                <small className="text-muted fw-bolder">Total Failed Transactions: {intransitIBTFailedTransactionState.length}</small>
            </div>
          </div> 
          )}

        <br></br>     
          <div>
                  { /* Intransit IBT Table Start */ }
                <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                  <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Doc Number</th>
                                    <th style={thStyle}>RFID</th>
                                    <th style={thStyle}>From Warehouse</th>
                                    <th style={thStyle}>To Warehouse</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Notes</th>
                                    <th style={{...thStyle, width: "100px"}}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                               { loading && viewMode === "default" ? (
                                 <>
                                    <tr>
                                        <td colSpan={7} className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        </td>
                                    </tr>
                                 </>
                                 ) : (
                                    <>
                                     {!selectedIntransitIBT && viewMode === "default" && intransitIBTState.filter((intransitIBT) => {
                                        if (nedapFilter === 'NEDAP') return intransitIBT.nedap_id;
                                        if (nedapFilter === 'NON_NEDAP') return !intransitIBT.nedap_id;
                                        return true; // ALL
                                    }).map((intransitIBT: IIntransitIBT) => { 
                                     const isNedapOnly = !!intransitIBT.nedap_id;
                                     return (
                                      <tr key={intransitIBT.id}>
                                        <td style={{...tdStyle, position: "relative"}}>
                                          {isNedapOnly && (
                                            <span style={{ position: "absolute", top: "1px", right: "1px", backgroundColor: "#3a4046", color: "white", fontSize: "8px", padding: "2px 6px", borderRadius: "10px", fontWeight: "bold", zIndex: 1 }}>
                                            RFID
                                            </span>
                                          )}
                                          {intransitIBT.doc_num}
                                        </td>
                                        <NedapIDCell nedap_id={intransitIBT.nedap_id} />
                                        <td style={tdStyle}>{intransitIBT.from_whs}</td>
                                        <td style={tdStyle}>{intransitIBT.to_whs}</td>
                                        <td style={tdStyle}>{intransitIBT.status}</td>
                                        <td style={tdStyle}>{formatDateFull(intransitIBT.created)}</td>
                                        <td style={tdStyle} title={intransitIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {intransitIBT.notes}
                                            </div>
                                        </td>
                                        <td style={tdStyle} className="d-flex gap-1">

                                             {/* Conditional Button Group */}
                                            {isNedapOnly ? (
                                                <>
                                                <DisabledScanBtn />
                                                <DisabledCheckBtn />
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View' onClick={() => handleViewClick(intransitIBT)}>
                                                    <FileText style={{ width: "17px", height: "17px" }}  /> 
                                                 </button>
                                                </>
                                            ) : (
                                                <>
                                                    {intransitIBT.status === "SENT" ? (
                                                        <button
                                                            className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                            title="Scan"
                                                            onClick={() => handleScanClick(intransitIBT)}
                                                        >
                                                            <ScanLine style={{ width: "17px", height: "17px" }} />
                                                        </button>
                                                        ) : (
                                                        <DisabledScanBtn />
                                                    )}

                                                    {intransitIBT.status === "SENT" ? (
                                                        <button
                                                            className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                            title="Submit"
                                                            onClick={() => handleReceivedStatusIntransitIBTClick(intransitIBT)}
                                                        >
                                                            <Check style={{ width: "17px", height: "17px" }} />
                                                        </button>
                                                    ) : (
                                                        <DisabledCheckBtn />
                                                    )}

                                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View' onClick={() => handleViewClick(intransitIBT)}>
                                                        <FileText style={{ width: "17px", height: "17px" }}  /> 
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                     </tr>
                                   )})}
                                 </>
                                )}

                                 {selectedIntransitIBT && viewMode !== "default" &&
                                    <>
                                        <tr key={selectedIntransitIBT.id}>
                                            <td style={tdStyle}>{selectedIntransitIBT.doc_num}</td>
                                            <td style={tdStyle}>{selectedIntransitIBT.nedap_id ? 'Yes' : 'No'}</td>
                                            <td style={tdStyle}>{selectedIntransitIBT.from_whs}</td>
                                            <td style={tdStyle}>{selectedIntransitIBT.to_whs}</td>
                                            <td style={tdStyle}>{selectedIntransitIBT.status}</td>
                                            <td style={tdStyle}>{formatDateFull(selectedIntransitIBT.created)}</td>
                                            <td style={tdStyle} title={selectedIntransitIBT?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {selectedIntransitIBT.notes}
                                            </div>
                                          </td>
                                          <td>

                                          </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={9} style={{ textAlign: 'center' }}>
                                                   { viewMode === 'view' ? (null) : (
                                                    <>
                                                    {selectedScannedItem && showCustomQuantitySection ? (
                                                        <form
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            handleQuantitySubmit();
                                                        }}
                                                        className="p-3 border rounded bg-light"
                                                        style={{ display: 'inline-block', textAlign: 'left', minWidth: '600px' }}
                                                        >
                                                        <div className="mb-3 position-relative">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0" style={{ fontWeight: 600 }}>Scanned Item Details</h6>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{ width: '28px', height: '28px' }}
                                                                onClick={handleCloseForm}
                                                                aria-label="Close"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                            </div>
                                                            <hr className="my-2" />
    
                                                            <div className="row">
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Name:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.name}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Item Group:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.item_group}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Item Code:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.item_code}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <label className="form-label mb-0"><strong>Barcode:</strong></label>
                                                                <div className="form-control-plaintext">{selectedScannedItem.barcode}</div>
                                                            </div>
                                                            </div>
                                                        </div>
    
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center">
                                                            <label htmlFor="custom_quantity" className="form-label mb-0 me-2"><strong>Quantity:</strong></label>
                                                                <input
                                                                type="number"
                                                                id="custom_quantity"
                                                                name="custom_quantity"
                                                                min={1}
                                                                value={customQuantity}
                                                                onChange={(e) => setCustomQuantity(e.target.value)}
                                                                className="form-control form-control-sm"
                                                                style={{ width: '100px' }}
                                                                />
                                                            </div>
    
                                                            <button type="submit" className="btn btn-dark btn-sm ms-3">
                                                            Save Qauntity
                                                            </button>
                                                        </div>
                                                        </form>
                                                    ) : (
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <span style={{ marginRight: '10px' }}>Scan</span>
                                                        <input
                                                        ref={scanRef}
                                                        id="scan_code"
                                                        name="scan_code"
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-md w-25"
                                                        style={{ margin: 0 }}
                                                        onKeyDown={(e) => handleScanKeyDown(e, selectedIntransitIBT.doc_num)}
                                                        />
                                                    </div>
                                                    )}
                                                </>
                                                )}
                                            </td>
                                         
                                        </tr>

                                    </>
                                }  
                            </tbody>
                       </table>
                   </div>
                  { /* Intransit IBT Table  End */ }

                  {intransitIBTLineState && viewMode !== "default" &&
                  <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                    <table className="table table-bordered">
                        <thead>
                        <tr>
                            <th style={thStyle}>Line No</th>
                            <th style={thStyle}>Item Name</th>
                            <th style={thStyle}>Item Code</th>
                            <th style={thStyle}>Item Barcode</th>
                            {selectedIntransitIBT?.status !== "SENT" && <th style={{...thStyle, width: '112px'}}>QTY On Hand</th>}
                            <th style={{...thStyle, width: '134px'}}>QTY Scanned Out</th>
                            {selectedIntransitIBT?.status !== "SENT" && <th style={{...thStyle, width: '100px'}}>QTY Sent</th>}
                            <th style={{...thStyle, width: '130px'}}>QTY Scanned In</th>
                            <th style={{...thStyle, width: '120px'}}>QTY Recieved</th>
                            {selectedIntransitIBT?.status === "SENT" && (
                                <th style={{...thStyle, width: "100px"}}>Actions</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                            <td
                                colSpan={selectedIntransitIBT?.status === "SENT" ? 9 : 9}
                                className="text-center"
                            >
                                <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                                </div>
                            </td>
                            </tr>
                        ) : (
                            intransitIBTLineState.map((intransitIBTLine: IIntransitIBTLine) => {
                            const { qty_scanned_out, qty_sent, qty_scanned_in, qty_received, qty_on_hand } = intransitIBTLine;
                            const isEditing = editingRowId === intransitIBTLine.id;

                            const hasValidationError =
                                selectedIntransitIBT?.status !== "SENT" &&
                                (
                                // Left-side checks: only compare if both are not null
                                (qty_sent != null && qty_scanned_out != null && qty_sent !== qty_scanned_out) ||
                                (qty_sent != null && qty_scanned_in != null && qty_scanned_in !== qty_sent) ||
                                (qty_scanned_in != null && qty_received != null && qty_received !== qty_scanned_in) ||

                                // Right-side checks: always compare, even if null
                                qty_sent !== qty_scanned_out ||
                                qty_scanned_out !== qty_scanned_in ||
                                qty_scanned_in !== qty_received
                                );

                            return (
                                <tr key={intransitIBTLine.id} style={{ backgroundColor: hasValidationError ? '#f7f7cd' : 'white' }}>
                                <td style={tdStyle}>{intransitIBTLine.line_num}</td>
                                <td style={tdStyle}>{intransitIBTLine.item_name}</td>
                                <td style={tdStyle}>{intransitIBTLine.item_code}</td>
                                <td style={tdStyle}>{intransitIBTLine.item_barcode}</td>
                                {selectedIntransitIBT?.status !== "SENT" && <td style={tdStyle} className="text-end">{qty_on_hand}</td>}
                                <td style={tdStyle} className="text-end">{qty_scanned_out}</td>
                                {selectedIntransitIBT?.status !== "SENT" && <td style={tdStyle} className="text-end">{qty_sent}</td>}
                                <td style={tdStyle} className="text-end">
                                    {isEditing ? (
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                        value={inputDraft[intransitIBTLine.id] ?? ''}
                                        onChange={(e) => handleQtyDraftChange(intransitIBTLine.id, e.target.value)}
                                        onBlur={() => handleBlur(intransitIBTLine.id)}
                                        autoFocus
                                        onClick={(e) => (e.target as HTMLInputElement).select()}
                                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                                        />
                                    </div>
                                    ) : (
                                            qty_scanned_in
                                    )}
                                </td>
                                <td style={tdStyle} className="text-end">{qty_received}</td>
                                {selectedIntransitIBT?.status === "SENT" && (
                                    <td style={tdStyle} className='d-flex gap-1'>
                                        {selectedIntransitIBT?.nedap_id === null ? (
                                        isEditing ? (
                                            <>
                                            {!inputDraft[intransitIBTLine.id]?.trim() ? (
                                                <DisabledSaveQtyBtn />
                                            ) : (
                                                <button
                                                className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                onClick={() => handleSaveClick(intransitIBTLine.id)}
                                                title="Save Quantity"
                                                >
                                                <Save style={{ width: "17px", height: "17px" }} />
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                onClick={() => setEditingRowId(null)}
                                                title="Cancel"
                                            >
                                                <X style={{ width: "17px", height: "17px" }} />
                                            </button>
                                            </>
                                        ) : editingRowId === null ? (
                                            <button
                                            className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                            onClick={() =>
                                                handleEditClick(
                                                intransitIBTLine.id,
                                                intransitIBTLine.qty_scanned_in ?? 0
                                                )
                                            }
                                            title="Edit Quantity"
                                            >
                                            <Pencil style={{ width: "17px", height: "17px" }} />
                                            </button>
                                        ) : (
                                            <DisabledEditQtyBtn />
                                        )
                                        ) : (
                                        // If nedap_id is NOT null → block editing
                                        <DisabledEditQtyBtn />
                                        )}
                                    </td>
                                    )}
                              </tr>
                            );
                            })
                        )}
                        </tbody>
                      </table>
                    </div>
                    }

         </div>                                  
  
     </CardBody>

          {/* Modal To Send Internal IBT - Start */}
            <>
            <Modal show={showUpdateStatusNormalIBTModal} onHide={() => { setShowUpdateStatusNormalIBTModal(false); }}>
                <Modal.Header closeButton>
                    <Modal.Title>
                       {`Receive Normal IBT [${selectedDocNum}]`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <p>
                     { "Are you sure you would like to received intransit IBT "}
                    </p>
                </div>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="primary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); handleRecievedIntransitIBTSubmit();  }}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { setShowUpdateStatusNormalIBTModal(false); } }>
                    Cancel
                </Button>
                </Modal.Footer>
            </Modal>
            </> 
         {/* Modal To Send Internal IBT - End */}


           {/* Modal Failed Transaction Rerun - Start */}
            <>
                <Modal show={showFailedIntransitIBTRerunModal} onHide={handleFailedIntransitIBTRerunResetClick}>
                    <Modal.Header closeButton>
                        <Modal.Title>{`Rerun Failed [${selectedIntransitIBTFailedTransaction?.doc_num}] Transaction`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <p>Are you sure you would like to rerun this transaction?</p>
                    </div>
                    </Modal.Body>
                    <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowFailedIntransitIBTRerunModal(false); handleFailedIntransitIBTRerunSubmit(); } }>
                        Confirm
                    </Button>
                    <Button variant="secondary" onClick={handleFailedIntransitIBTRerunResetClick}>
                        Cancel
                    </Button>
                    </Modal.Footer>
                </Modal>
            </> 
            {/*  Modal Failed Transaction Rerun - End */}
    
    
            {/* Modal Failed Transaction Handled - Start */}
            <>
                <Modal show={showFailedIntransitIBTHandledModal} onHide={handleFailedIntransitIBTHandledResetClick}>
                    <Modal.Header closeButton>
                        <Modal.Title>{`Handle Failed [${selectedIntransitIBTFailedTransaction?.doc_num}] Transaction`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <p>Are you sure you would like to mark this transaction as handled?</p>
                    </div>
                    </Modal.Body>
                    <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowFailedIntransitIBTHandledModal(false); handleFailedIntransitIBTHandledSubmit(); } }>
                        Confirm
                    </Button>
                    <Button variant="secondary" onClick={handleFailedIntransitIBTHandledResetClick}>
                        Cancel
                    </Button>
                    </Modal.Footer>
                </Modal>
            </> 
            {/*  Modal Failed Transaction Handled - End */}


   </Card>
  </>
  )
}
